/* eslint-disable @typescript-eslint/no-unused-vars */
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import {
  Button,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import PeopleIcon from '@material-ui/icons/People'
import { useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import 'yet-another-react-lightbox/styles.css'

import NoPictureDeafault from '../../../../assets/images/NoPictureDeafault.png'
import insertPhoto from '../../../../assets/images/insertPhoto.svg'
import { sameEncodedCode } from '../../../../helpers/encodedCode'
import { getTranslate } from '../../../../localization'
import { CartResponse, ItemsInCart } from '../../../../types/cart'
import { Currency } from '../../../../types/currency'
import {
  Entities,
  PaymentTermsBalance,
  RoomForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import { CreditorListItem } from '../../../../types/walletCreditor'
import SimpleFacility from '../Facility'
import RatePlanRow from '../RatePlanRow'
import useStyles from './styles/index.styles'

type Props = {
  room: RoomForSearch
  currency: Currency
  allCreditors: {
    walletName: string
    walletID: number
    creditorItems: CreditorListItem[]
  }[]
  itemsInCart: ItemsInCart
  setItemsInCart: React.Dispatch<React.SetStateAction<ItemsInCart>>
  searchAvailabilityInput: SearchAvailabilityInput
  getCart: LazyQueryExecFunction<
    {
      cartResponse: CartResponse
    },
    OperationVariables
  >
  loadingForGetCart: boolean
  setSelectedItemHash: React.Dispatch<React.SetStateAction<string>>
  selectedItemHash: string
  cartData:
    | {
        cartResponse: CartResponse
      }
    | undefined
  setSearchData: (value: React.SetStateAction<Entities | undefined>) => void
  roomTypeIndex: number
  searchData: Entities | undefined
  nonBillateralWallet: {
    walletName: string
    walletID: number
    creditorID?: number
    cashBalance: number | 0
    remainigCreditBalance: number | 0
  }
}

const RoomTypeDetails = ({
  room,
  currency,
  allCreditors,
  itemsInCart,
  setItemsInCart,
  searchAvailabilityInput,
  getCart,
  loadingForGetCart,
  selectedItemHash,
  setSelectedItemHash,
  cartData,
  nonBillateralWallet
}: Props) => {
  const classes = useStyles()
  const {
    roomTypeName,
    roomTypeCode,
    images,
    ratePlans,
    occupancy,
    facilities
  } = room
  const MAX_VISIBLE = 10
  const visibleFacilities = facilities.slice(0, MAX_VISIBLE)
  const hiddenFacilities = facilities.slice(MAX_VISIBLE)
  const [showAllFacility, setShowAllFacility] = useState(false)
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const slides = images.map((img) => ({
    src: img
  }))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <div
            onClick={() => setOpen(true)}
            className={[
              classes.imageContainer,
              slides.length > 1 && classes.imageContainerPointer
            ].join(' ')}>
            <img
              src={images.length > 0 ? images[0] : NoPictureDeafault}
              className={classes.image}
            />
            {slides.length > 1 && (
              <div className={classes.counter}>
                <img src={insertPhoto} className={classes.photoIcon} />
                <Typography variant="h5" className={classes.iconText}>
                  {` +${slides.length - 1}`}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.details}>
            <Typography variant="subtitle1" className={classes.sub}>
              {`${roomTypeName}`}
            </Typography>
            <div className={classes.individual}>
              <div className={classes.guest}>
                <PeopleIcon className={classes.icon} />
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  className={classes.headerText}>
                  {`${occupancy.adults} ${getTranslate('Adults')}`}
                </Typography>
              </div>
              <Chip
                label={
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    className={classes.headerText}>
                    {`${getTranslate('Max')} : ${
                      occupancy.total
                    } ${getTranslate('Adults')}`}
                  </Typography>
                }
                size="small"
              />
            </div>
            {facilities && facilities.length > 0 && (
              <div>
                {isMobile ? (
                  <>
                    <Button
                      variant="text"
                      size="small"
                      className={classes.btn}
                      onClick={() => setShowAllFacility(!showAllFacility)}>
                      {showAllFacility
                        ? getTranslate('Show Less Facilities')
                        : getTranslate('Show All Facilities')}
                    </Button>

                    {showAllFacility && (
                      <div className={classes.facilityContainer}>
                        {facilities.map((fac, i) => (
                          <SimpleFacility title={fac.Name} key={i} />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" className={classes.sub}>
                      {getTranslate('Facilities')}
                    </Typography>

                    <div className={classes.facilityContainer}>
                      {visibleFacilities.map((fac, i) => (
                        <SimpleFacility title={fac.Name} key={i} />
                      ))}

                      {hiddenFacilities.length > 0 && (
                        <Tooltip
                          placement="top"
                          title={
                            <div>
                              {hiddenFacilities.map((fac, i) => (
                                <Typography variant="body2" key={i}>
                                  {getTranslate(fac.Name)}
                                </Typography>
                              ))}
                            </div>
                          }>
                          <div className={classes.showMore}>
                            <Typography
                              variant="h5"
                              style={{ fontWeight: 600 }}>
                              {`+${hiddenFacilities.length} ${getTranslate(
                                'more'
                              )}`}
                            </Typography>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={classes.content}>
          {!isMobile ? (
            <div className={classes.tableHeader}>
              <Typography className={classes.sub}>
                {getTranslate('Rate Plans')}
              </Typography>
              <Typography
                className={[classes.sub, classes.middleText].join(' ')}>
                {getTranslate(`Today's Price`)}
              </Typography>
            </div>
          ) : (
            <Typography variant="h4" className={classes.headerforRateplans}>
              {getTranslate('Rate Plans')}
            </Typography>
          )}
          {ratePlans.map((rp, index) => {
            const matchingCreditorItems = allCreditors.flatMap((group) =>
              group.creditorItems
                .filter((item) =>
                  sameEncodedCode(item.entity.entityCode, rp.providerCode)
                )
                .map((item) => ({
                  ...item,
                  walletID: group.walletID,
                  walletName: group.walletName
                }))
            )

            let paymentTermsBalance: PaymentTermsBalance[] = []
            if (localStorage.getItem('WalletType') === 'ProviderCredit') {
              paymentTermsBalance = matchingCreditorItems.map((item) => ({
                walletName: item.walletName,
                walletID: item.walletID,
                creditorID: item.creditorID,
                cashBalance: item.walletBalance,
                remainigCreditBalance: item.remainingCreditBalance
              }))
            } else {
              paymentTermsBalance.push(nonBillateralWallet)
            }

            return (
              <RatePlanRow
                key={index}
                ratePlan={rp}
                disable={
                  !rp.availablePaymentMethods.includes(
                    localStorage.getItem('WalletType') || ''
                  )
                }
                currency={currency}
                paymentTermsBalance={paymentTermsBalance}
                roomTypeCode={roomTypeCode}
                occupancy={occupancy}
                searchAvailabilityInput={searchAvailabilityInput}
                getCart={getCart}
                loadingForGetCart={loadingForGetCart}
                selectedItemHash={selectedItemHash}
                setSelectedItemHash={setSelectedItemHash}
                cartData={cartData?.cartResponse}
                itemsInCart={itemsInCart}
                setItemsInCart={setItemsInCart}
              />
            )
          })}
        </div>
      </div>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        thumbnails={{
          position: 'bottom',
          width: 100,
          height: 100,
          imageFit: 'cover'
        }}
        plugins={[Thumbnails]}
        carousel={{
          finite: true
        }}
        styles={{
          container: {
            backgroundColor: 'black'
          }
        }}
      />
    </>
  )
}

export default RoomTypeDetails
